'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { useAppDescription } from '@/app/hooks/useAppDescription';
import { iconForApp } from '@/app/features/workspace/utils/appIcon';
import { formatFullTime } from '@/app/utils/relativeTime';
import TileStarButton from './TileStarButton';
import type { UserAppAccessSummaryDto } from '@/app/services/api/userAppAccessService';

/**
 * Mobile-only horizontal-scroll "Recents" strip at the top of MobileWorkspaceHome.
 * Hidden entirely when the user has zero tracked recents (Requirement 3.6).
 * Matches iOS Files Recents tab / GitHub mobile "Recent repositories" / Notion mobile home.
 */

interface RecentsSectionProps {
  recents: UserAppAccessSummaryDto[];
  isLoading: boolean;
  onTileClick: (app: UserAppAccessSummaryDto) => void;
}

const RecentsSection: React.FC<RecentsSectionProps> = React.memo(({
  recents,
  isLoading,
  onTileClick,
}) => {
  const { t } = useTranslation('workspace');
  const { getAppText } = useAppDescription();

  // Hide entirely on zero recents. Loading still renders skeletons so the
  // layout doesn't jump on arrival.
  if (!isLoading && recents.length === 0) {
    return null;
  }

  return (
    <section aria-label={t('recents.heading')} className="space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {t('recents.heading')}
      </h2>
      <div
        className={cn(
          '-mx-4 px-4 flex gap-3 overflow-x-auto',
          'snap-x snap-mandatory scroll-smooth',
          '[&::-webkit-scrollbar]:hidden'
        )}
        style={{ scrollbarWidth: 'none' }}
        aria-busy={isLoading}
      >
        {isLoading && recents.length === 0
          ? [0, 1, 2].map((i) => (
              <div
                key={`skeleton-${i}`}
                className="h-24 w-20 flex-none rounded-xl bg-muted animate-pulse snap-start"
                aria-hidden
              />
            ))
          : recents.map((app) => {
              const text = getAppText(app.appCode, app.appName, app.description, app.id);
              const Icon = iconForApp(app);
              const fullTime = formatFullTime(app.lastAccessedAt);
              return (
                <div key={app.id} className="relative flex-none snap-start">
                  <button
                    type="button"
                    onClick={() => onTileClick(app)}
                    title={fullTime ? `${text.name} — last opened: ${fullTime}` : text.name}
                    className={cn(
                      'touch-target',
                      'h-24 w-20 flex flex-col items-center justify-center gap-1 p-2',
                      'rounded-xl border border-border/60 bg-card',
                      'hover:bg-accent/40 active:bg-accent/60 transition-colors'
                    )}
                  >
                    <Icon className="w-6 h-6 text-primary" />
                    <span className="text-xs font-medium text-center line-clamp-2 w-full leading-tight">
                      {text.name}
                    </span>
                  </button>
                  <TileStarButton sysAppId={app.id} />
                </div>
              );
            })}
      </div>
    </section>
  );
});

RecentsSection.displayName = 'RecentsSection';
export default RecentsSection;
